import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ShieldCheck, ShieldOff, User, Crown, ToggleLeft, ToggleRight } from 'lucide-react';

const PERMISSION_LABELS = {
  content: 'Site Content',
  gallery: 'Gallery',
  blog: 'Blog Posts',
  founder: 'Founder',
  contact: 'Contact Info',
  messages: 'Messages',
  activity_registrations: 'Activity Registrations',
  donations: 'Donations',
  stripe_settings: 'Stripe Settings',
};

const ASSIGNABLE_PERMISSION_KEYS = ['content', 'gallery', 'blog', 'founder', 'contact', 'messages', 'activity_registrations', 'donations'];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPerms, setShowPerms] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', password: '', permissions: [...ASSIGNABLE_PERMISSION_KEYS] });
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error('Email and password required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/users', newUser);
      toast.success('Admin created');
      setShowCreate(false);
      setNewUser({ email: '', password: '', permissions: [...ASSIGNABLE_PERMISSION_KEYS] });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally { setSaving(false); }
  };

  const handleToggleSuspend = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { is_suspended: !user.is_suspended });
      toast.success(user.is_suspended ? 'Account activated' : 'Account suspended');
      fetchUsers();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.email}?`)) return;
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success('Admin deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleTogglePerm = async (user, perm) => {
    const current = user.permissions || [];
    const newPerms = current.includes(perm)
      ? current.filter(p => p !== perm)
      : [...current, perm];
    try {
      await api.put(`/admin/users/${user.id}`, { permissions: newPerms });
      toast.success('Permissions updated');
      fetchUsers();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#1E4FA1] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-users-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>User Management</h1>
          <p className="text-[#94A3B8] text-sm mt-1">Manage admin accounts and permissions</p>
        </div>
        <Button data-testid="create-admin-btn" onClick={() => setShowCreate(true)} className="w-full bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl sm:w-auto">
          <Plus size={16} className="mr-2" /> Add Admin
        </Button>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {users.map((user) => (
          <div key={user.id} data-testid={`user-item-${user.id}`}
            className="bg-white rounded-2xl border border-[#E2E8F0] p-5">
            <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-start gap-3 sm:items-center">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${user.role === 'super_admin' ? 'bg-amber-100' : 'bg-[#EBF2FA]'}`}>
                  {user.role === 'super_admin' ? <Crown size={18} className="text-amber-600" /> : <User size={18} className="text-[#1E4FA1]" />}
                </div>
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="min-w-0 break-all font-semibold text-[#0F172A]">{user.email}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      user.role === 'super_admin'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-[#EBF2FA] text-[#1E4FA1]'
                    }`}>
                      {user.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                    </span>
                    {user.is_suspended && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Suspended</span>
                    )}
                  </div>
                  <p className="text-xs text-[#94A3B8] mt-0.5">
                    {user.role === 'super_admin' ? 'Full access' : `${(user.permissions || []).length} permissions`}
                  </p>
                </div>
              </div>

              {user.role !== 'super_admin' && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowPerms(user)} className="rounded-lg" data-testid={`edit-perms-${user.id}`}>
                    <Edit size={14} className="mr-1" /> Permissions
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleToggleSuspend(user)} className="rounded-lg"
                    data-testid={`toggle-suspend-${user.id}`}>
                    {user.is_suspended ? <ShieldCheck size={14} /> : <ShieldOff size={14} />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} className="text-red-500 hover:bg-red-50 rounded-lg"
                    data-testid={`delete-user-${user.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              )}
            </div>

            {/* Permissions Tags for non-super admins */}
            {user.role !== 'super_admin' && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(user.permissions || []).map(perm => (
                  <span key={perm} className="text-xs px-2.5 py-1 rounded-full bg-[#F8FAFC] text-[#475569] border border-[#E2E8F0]">
                    {PERMISSION_LABELS[perm] || perm}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Admin Account</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input data-testid="new-admin-email" value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="admin@example.com" className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
                <Input data-testid="new-admin-password" type="password" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 10 characters" className="rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Permissions</label>
              <div className="space-y-2">
                {ASSIGNABLE_PERMISSION_KEYS.map((key) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newUser.permissions.includes(key)}
                      onChange={() => {
                        const perms = newUser.permissions.includes(key)
                          ? newUser.permissions.filter(p => p !== key)
                          : [...newUser.permissions, key];
                        setNewUser({ ...newUser, permissions: perms });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{PERMISSION_LABELS[key]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-xl">Cancel</Button>
              <Button data-testid="save-new-admin" onClick={handleCreate} disabled={saving}
                className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl">
                {saving ? 'Creating...' : 'Create Admin'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={!!showPerms} onOpenChange={() => setShowPerms(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="mscm-text-safe">Edit Permissions: {showPerms?.email}</DialogTitle></DialogHeader>
          {showPerms && (
            <div className="space-y-3 mt-4">
              {ASSIGNABLE_PERMISSION_KEYS.map((key) => {
                const hasPerm = (showPerms.permissions || []).includes(key);
                return (
                  <button
                    key={key}
                    data-testid={`toggle-perm-${key}`}
                    onClick={() => handleTogglePerm(showPerms, key)}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                  >
                    <span className="text-sm font-medium text-[#0F172A]">{PERMISSION_LABELS[key]}</span>
                    {hasPerm ? (
                      <ToggleRight size={24} className="text-[#1E4FA1]" />
                    ) : (
                      <ToggleLeft size={24} className="text-[#94A3B8]" />
                    )}
                  </button>
                );
              })}
              <div className="flex justify-end pt-2">
                <Button onClick={() => setShowPerms(null)} className="bg-[#1E4FA1] hover:bg-[#153A7A] text-white rounded-xl">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
